import React from "react";
import GuideLayout from "../../components/layout/GuideLayout";
import Section from "../../components/layout/Section";
import Subheader from "../../components/layout/Subheader";
import {
  Typography,
  Link,
  Box
} from "@mui/material";
import BulletList from "../../components/layout/BulletList";
import { useTheme } from "@mui/material/styles";
import CodeBlock from "../../components/layout/CodeBlock";

const highlights = [
  "Token search",
  "Token trading guide",
  "Item images now on Stylisher!",
  "Lebronbot notes",
  "Drop Rate Simulator",
];

const November2025 = () => {
  const theme = useTheme();

  return (
    <GuideLayout sections={[]}>
      <Section id="intro" title="Token Search + More!">
        <Typography
          variant="caption"
          color="secondary"
          sx={{ mt: -1, mb: 2, display: "block" }}
        >
          November 2025 Update
        </Typography>
        <Typography paragraph>
          Based on feedback over the last two months, we've added some new features and content to help with your trading experience. Please welcome our new <Link href="/token-search" sx={{ color: theme.palette.primary.main }}>token search</Link> and our guide on <Link href="/guides/token-trading" sx={{ color: theme.palette.primary.main }}>token trading</Link>. We know token trading can be really confusing, so we hope these can give you some more direction.
        </Typography>

        That's not all we've done though, so here's some more highlights:
        <BulletList items={highlights} />

        <Subheader title="Tokens, tokens, tokens!" />
        <Typography paragraph>
          Tokens are a large part of NC trading now, and with so many releases, it can be hard to keep up. We've been working on this <Link href="/guides/token-trading" sx={{ color: theme.palette.primary.main }}>token trading guide</Link> for a while now, so please give it a read if you're new/confused on token values!
        </Typography>

        <Typography paragraph>
          In addition, we're also launching our <Link href="/token-search" sx={{ color: theme.palette.primary.main }}>token search</Link> page, where you can search tokens by color/species/type. We've gotten a few requests for a more general search, so we've done a bit of restructuring to make it happen! On that page, you can search the recent token trades by prismatic color, base color, and/or species. If you'd like to see just base tokens, we've added an option to exclude prismatic trades. On the other hand, if you'd like to see all prismatics and no base tokens, we've added that too! Essences are also included in this, but they are their own special category, so they will not show up in species search. As a reminder, this is just another tool - it is certainly okay to disagree with values especially as token values fluctuate with each rotation.
        </Typography>

        <Typography paragraph>
          Our token search is only available on our website as we have more flexibility with search options here. We've had inquiries about our discordbot, but at this time, we are unable to bring token search to lebronbot due to external limitations. We'll look into a potential modified version of token search for the future, but that'll remain in drafts until we find a suitable solution.
        </Typography>

        <Subheader title="Item Images" />
        <Typography paragraph>
          Item images are now on Stylisher! When you search for an item, you'll now see the item image with it for your convenience. We don't anticipate any big issues with this, but be aware that we have a placeholder image that might pop up from time to time. This would mainly happen for newer items on first day releases, where we'd have the item name, but not necessarily the image yet. If that happens, just visit again later when we've grabbed the item image.
        </Typography>

        <Box sx={{ textAlign: "center" }}>
          {/* eslint-disable-next-line */}
          <img src={require('../../assets/search_with_image.png')} alt="example of a search with an item image" style={{ maxWidth: "80%", borderRadius: 8, marginBottom: 10 }} />
        </Box>

        <Typography paragraph>
          We'd also like to thank <Link href="https://itemdb.com.br/" sx={{ color: theme.palette.primary.main }}>ItemDB</Link> for allowing us an easy way to make these item images happen :)
        </Typography>

        <Subheader title="Lebronbot Notes" />
        <Typography paragraph>
          Funny story... turns out there is a way to add Lebronbot as a server member. We've been having people add Lebronbot as an app, which is technically fine because you're still able to use all the commands, but the bot will not show up as an actual member in your server. Our team has been informed now that there's actually a link in developer settings that people can use to add it as a member. First time making and managing a discord bot, so totally our bad on this, sorry! Please feel free to continue adding lebronbot as an app or use the link to have it in your members list - whatever you prefer!
          <CodeBlock text="https://discord.com/oauth2/authorize?client_id=1378407906361344181&permissions=277025483776&integration_type=0&scope=bot+applications.commands" />

        </Typography>

        <Typography paragraph>
          We're also aware that there's frustration with the strict formatting, especially on mobile where it can be a pain to retype a trade. Rather than just displaying the first detected error, lebronbot will now repeat your input back to you. We hope this is a suitable compromise so no one has to completely lose their progress on a trade report over a typo!
        </Typography>
        <Box sx={{ textAlign: "center" }}>
          <img src={require('../../assets/lebron/error_repeats_input.png')} alt="example of error message displaying user input" style={{ maxWidth: "80%", borderRadius: 8, marginBottom: 5 }} />
        </Box>

        <Subheader title={"Drop Rate Simulator"} />
        <Typography paragraph>
          Do you want to "open" a few caps for fun? Check out our <Link href="/articles/drop-rate-simulator" sx={{ color: theme.palette.primary.main }}>drop rate simulator</Link>! We have examples of caps and other chance items set to the official published drop rates to try out. If those don't suit you, we included a custom simulator where you can input your own images and item names. This is just a little fun feature for anyone itching to test their luck risk free :) 
        </Typography>
      </Section>

    </GuideLayout>
  );
};

export default November2025;
